import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  Text,
  FormLabel,
  IconButton,
  InputGroup,
  Td,
  Tr,
} from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import { FormikProps, getIn, setIn } from 'formik';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';

import {
  checkCashAdvanceRateConfigErrors,
  checkMaximumPrefundingAmountConfigErrors,
} from '@finley-technologies/calc-agent';
import {
  date as commonDate,
  error as commonError,
  finleyAssert,
  finleyAssertUnreachable,
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import {
  Bank,
  CreditFacility,
  FormulaEngine,
  FundingRequest,
  Report,
  Util,
} from '@finley-technologies/finley-types';

import { AccountTypeGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  bankAccountTypes,
  borrowingBaseContributionAccountTypes,
} from '../../consts';
import { WipConfig } from '../../types';
import { FormulaEditor } from '../edit/MonacoEditor/ui/FormulaEditor';
import { checkHasNoEffect } from '../utils';
import { NoEffectTooltip } from './NoEffectTooltip';
import {
  CheckboxFormField,
  FormFieldLabel,
  InputFormField,
  NumberInputFormField,
} from 'packages/common-frontend/components/FormField';
import {
  formatSelectOptions,
  Select,
} from 'packages/common-frontend/components/Select';
import * as utils from 'packages/common-frontend/utils';
import {
  CreditAgreement,
  getBooleanSymbol,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteArrayPropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { DeletePropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeletePropertyButton';
import { EditButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditButton';
import { EditLinkedAccountModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditLinkedAccountModal';
import {
  EditPropertyModal,
  EditPropertyModalOnSave,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditPropertyModal';
import { defaultTheme } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/MonacoEditor/ui/common';
import { useCreditAgreementContext } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/hooks';
import { RecurringDateConfigView } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/property/RecurringDateConfigView';

export interface BasePropertyParams {
  propertyKey: string;
  wipConfig?: WipConfig;
  isOptional: boolean;
  basePropertyKey: string;
  fullPropertyKey: string;
  idx?: number;
}

interface MutatePropertyParams extends BasePropertyParams {
  configChildren?: (localFormProps: FormikProps<any>) => React.ReactElement;
  wipConfig: WipConfig;
}

type MutatePropertyProps = {
  mutatePropertyParams: MutatePropertyParams;
  children?: (localFormProps: FormikProps<any>) => React.ReactElement;
  onSave?: EditPropertyModalOnSave;
};
export const MutateProperty = ({
  mutatePropertyParams: {
    wipConfig,
    fullPropertyKey,
    basePropertyKey,
    propertyKey,
    isOptional,
    idx,
    configChildren,
  },
  children,
  onSave,
}: MutatePropertyProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <EditButton setIsOpen={setIsOpen} />
      {isOptional && _.isNumber(idx) && (
        <DeleteArrayPropertyButton
          wipConfig={wipConfig}
          basePropertyKey={`${basePropertyKey}.${propertyKey}.contents`}
          idx={idx}
        />
      )}
      {isOptional && !_.isNumber(idx) && (
        <DeletePropertyButton
          formProps={wipConfig.formProps}
          basePropertyKey={basePropertyKey}
          propertyKey={propertyKey}
        />
      )}
      <EditPropertyModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        wipConfig={wipConfig}
        basePropertyKey={fullPropertyKey}
        configChildren={configChildren}
        onSave={onSave}
      >
        {children ? (localFormProps) => children(localFormProps) : undefined}
      </EditPropertyModal>
    </>
  );
};

type BasePropertyTableRowProps = {
  property: CreditAgreement.DomainEntity.BaseProperty;
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  columnWidth?: number;
  accordion?: boolean;
  hasNoEffectLabel?: boolean;
  children: React.ReactNode;
};
export const BasePropertyTableRow = ({
  property,
  headers,
  columnWidth,
  accordion = false,
  hasNoEffectLabel = false,
  children,
}: BasePropertyTableRowProps) => {
  const { name, section, description, _note_, _isWrittenIn_ } = property;
  const columnWidthStr = `${columnWidth}%`;

  return (
    <Tr>
      <Td w={columnWidthStr}>
        <Box>{name}</Box>
        {hasNoEffectLabel ? <NoEffectTooltip /> : null}
      </Td>
      <Td w={columnWidthStr}>{React.Children.toArray(children)[0]}</Td>
      {_.includes(headers, 'section') && (
        <Td w={columnWidthStr}>{section || '-'}</Td>
      )}
      {_.includes(headers, 'description') && (
        <Td w={columnWidthStr}>{description || '-'}</Td>
      )}
      {_.includes(headers, '_note_') && (
        <Td w={columnWidthStr}>{_note_ || '-'}</Td>
      )}
      {_.includes(headers, '_isWrittenIn_') && (
        <Td w={columnWidthStr}>
          <>{getBooleanSymbol(_isWrittenIn_)}</>
        </Td>
      )}
      {React.Children.toArray(children)[1] && (
        <Td w={columnWidthStr}>{React.Children.toArray(children)[1]}</Td>
      )}
      {accordion && <Td w="5%" />}
    </Tr>
  );
};

const cashAdvanceRateShell = {
  _isWrittenIn_: true,
  description: '100%',
  type: 'cashAdvanceRateProperty',
  name: 'Cash Advance Rate',
  value: '100%',
  _config_: {
    cashAdvanceRateConfig: {
      type: 'formula',
      config: {
        engineVersion: 'hyperFormula1',
        formula: '1',
      },
    },
  },
};
const getAvailableAccountTypesOptions = (
  basePropertyParams: BasePropertyParams,
  currentValue: AccountTypeGQLType,
) => {
  const { wipConfig, basePropertyKey } = basePropertyParams;
  // remove the last array ref to get all bankAccounts key
  const bankAccountsPropertyKey = basePropertyKey
    .split('[')
    .slice(0, -1)
    .join('[');
  const currentBankAccounts = getIn(
    wipConfig?.formProps.values,
    bankAccountsPropertyKey,
  );
  const currentAccountTypes = currentBankAccounts.map((ba: any) =>
    getIn(ba, 'type.value'),
  );
  const availableAccountTypes = bankAccountTypes.filter(
    (baType) => !currentAccountTypes.includes(baType),
  );

  if (currentValue) availableAccountTypes.push(currentValue);

  const selectOptions = formatSelectOptions(availableAccountTypes, 'sentence');

  return selectOptions;
};
type BankAccountPropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  property: CreditAgreement.DomainEntity.BankAccountTypeProperty;
  basePropertyParams: BasePropertyParams;
  columnWidth?: number;
  accordion?: boolean;
};
export const BankAccountProperty = ({
  headers,
  property,
  basePropertyParams,
  columnWidth,
  accordion,
}: BankAccountPropertyProps) => {
  const { wipConfig, basePropertyKey, fullPropertyKey } = basePropertyParams;

  const onSave = (props: any) => {
    const cashAdvanceRateKey = `${basePropertyKey}.cashAdvanceRate`;
    const wip = commonUtils.ensure(wipConfig, 'Expected wipConfig to be exist');
    const accountType = getIn(props, `value`);
    const hasCashAdvanceRateProperty = getIn(wip.wipShell, cashAdvanceRateKey);

    // if is a contributing account type, create the cashAdvanceRate property if it does not exist, otherwise remove it
    if (
      borrowingBaseContributionAccountTypes.includes(accountType) &&
      !hasCashAdvanceRateProperty
    ) {
      // needs to be set in both places since setFieldValue is async-ish using hooks
      wip.formProps.setFieldValue(cashAdvanceRateKey, cashAdvanceRateShell);
      return setIn(
        wip.formProps.values,
        cashAdvanceRateKey,
        cashAdvanceRateShell,
      );
    }
    if (!borrowingBaseContributionAccountTypes.includes(accountType)) {
      wip.formProps.setFieldValue(cashAdvanceRateKey, undefined);
      return setIn(wip.formProps.values, cashAdvanceRateKey, undefined);
    }

    return wip.formProps.values;
  };

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
    >
      {`${commonUtils.toSentenceCase(property.value)}`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
          onSave={onSave}
        >
          {({ setFieldValue, values: localValues }) => (
            <Flex alignItems="flex-end" width="100%">
              <FormFieldLabel labelWidth="60" label="Value" />{' '}
              <InputGroup width="100%">
                <Select
                  value={localValues.value}
                  onChange={(val) => {
                    setFieldValue('value', val);
                  }}
                  options={getAvailableAccountTypesOptions(
                    basePropertyParams,
                    localValues.value,
                  )}
                  width="sm"
                />
              </InputGroup>
            </Flex>
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

const engineTypeOptions = formatSelectOptions(
  _.values(FormulaEngine.FullSupportedFormulaEngines),
  'title',
);
type CashAdvanceRateConfigProps = {
  localFormProps: FormikProps<any>;
  validationErrors: string[];
  setValidationErrors: React.Dispatch<React.SetStateAction<string[]>>;
};
const CashAdvanceRateConfig = ({
  localFormProps,
  validationErrors,
  setValidationErrors,
}: CashAdvanceRateConfigProps) => {
  return (
    <>
      <Flex width="100%">
        <FormLabel w="60">Formula Engine</FormLabel>
        <InputGroup width="100%">
          <Select
            value={
              localFormProps.values._config_.cashAdvanceRateConfig.config
                .engineVersion
            }
            options={engineTypeOptions}
            onChange={(engineVersion) => {
              localFormProps.setFieldValue(
                '_config_.cashAdvanceRateConfig.config.engineVersion',
                engineVersion,
              );
            }}
          />
        </InputGroup>
      </Flex>
      {!_.isEmpty(validationErrors) && (
        <Flex width="100%">
          <Alert status="error">
            <AlertIcon />
            <AlertDescription>
              Errors:
              {validationErrors.join(',')}
            </AlertDescription>
          </Alert>
        </Flex>
      )}
      <Flex width="100%">
        <FormLabel w="60">Formula</FormLabel>
        <FormulaEditor
          language="formula-cashAdvanceRate"
          resize="vertical"
          value={
            localFormProps.values._config_.cashAdvanceRateConfig.config.formula
          }
          onChange={(formula) => {
            localFormProps.setFieldValue(
              '_config_.cashAdvanceRateConfig.config.formula',
              formula,
            );

            const cashAdvanceRateConfig = _.set(
              { ...localFormProps.values._config_.cashAdvanceRateConfig },
              'config.formula',
              formula,
            );

            setValidationErrors(
              checkCashAdvanceRateConfigErrors({
                cashAdvanceRateConfig,
              }).errors,
            );
          }}
        />
      </Flex>
    </>
  );
};

type CashAdvanceRatePropertyProps = {
  property: CreditAgreement.DomainEntity.CashAdvanceRateProperty;
  basePropertyParams: BasePropertyParams;
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  columnWidth?: number;
  accordion?: boolean;
};
const CashAdvanceRateProperty = ({
  property,
  basePropertyParams,
  headers,
  columnWidth,
  accordion,
}: CashAdvanceRatePropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;
  const [validationErrors, setValidationErrors] = useState(
    wipConfig
      ? checkCashAdvanceRateConfigErrors({
          cashAdvanceRateConfig: property._config_.cashAdvanceRateConfig,
        }).errors
      : [],
  );
  const configChildren = (localFormProps: FormikProps<any>) => {
    return (
      <CashAdvanceRateConfig
        localFormProps={localFormProps}
        validationErrors={validationErrors}
        setValidationErrors={setValidationErrors}
      />
    );
  };

  const onSave = () => {
    finleyAssert(
      !validationErrors.length,
      new commonError.FinleyError(
        'Cannot save cash advance rate formula with errors',
      ),
    );

    const wip = commonUtils.ensure(wipConfig, 'Expected wipConfig to be exist');
    return wip.formProps.values;
  };

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
    >
      {`${property.value}`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{
            ...basePropertyParams,
            wipConfig,
            configChildren,
          }}
          onSave={onSave}
        >
          {(localFormProps) => (
            <>
              <InputFormField
                propName="value"
                label="Value"
                formProps={localFormProps}
                labelWidth="60"
                inputWidth="sm"
                flexDirection="row"
                alignItems="flex-end"
              />
            </>
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

type BooleanPropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  property: CreditAgreement.DomainEntity.BooleanProperty;
  basePropertyParams: BasePropertyParams;
  columnWidth?: number;
  accordion?: boolean;
  hasNoEffectLabel?: boolean;
};
export const BooleanProperty = ({
  headers,
  property,
  basePropertyParams,
  columnWidth,
  accordion,
  hasNoEffectLabel,
}: BooleanPropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
      hasNoEffectLabel={hasNoEffectLabel}
    >
      {`${property.value}`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <CheckboxFormField
              propName="value"
              label="Value"
              formProps={localFormProps}
              value={localFormProps.values.value}
              labelWidth="60"
              inputWidth="sm"
              flexDirection="row"
            />
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

const getComponentClassAllocationMethod = (
  basePropertyKey: string,
):
  | typeof FundingRequest.ClassCollectionsPaydownAllocationMethod
  | typeof FundingRequest.ClassCurePaydownAllocationMethod
  | typeof FundingRequest.ClassDrawRequestAllocationMethod => {
  const componentType = basePropertyKey.split('.').slice(-1)[0];

  finleyAssert(
    commonTypeguards.isFundingRequestComponentType(componentType),
    `unexpected component type: ${componentType}`,
  );

  switch (componentType) {
    case FundingRequest.FundingRequestComponentType.CollectionsPaydown:
      return FundingRequest.ClassCollectionsPaydownAllocationMethod;
    case FundingRequest.FundingRequestComponentType.CurePaydown:
      return FundingRequest.ClassCurePaydownAllocationMethod;
    case FundingRequest.FundingRequestComponentType.DrawRequest:
      return FundingRequest.ClassDrawRequestAllocationMethod;
    case FundingRequest.FundingRequestComponentType.CollectionsRecycle:
      throw new commonError.FinleyError(
        'invalid funding request component type for class allocation method',
        {
          metadata: { logMeta: { componentType } },
        },
      );
    default:
      return finleyAssertUnreachable(componentType);
  }
};

type ClassAllocationMethodPropertyProps = {
  property: CreditAgreement.DomainEntity.ClassAllocationMethodProperty;
  basePropertyParams: BasePropertyParams;
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  columnWidth?: number;
  accordion?: boolean;
  hasNoEffectLabel?: boolean;
};
const ClassAllocationMethodProperty = ({
  property,
  basePropertyParams,
  headers,
  columnWidth,
  accordion,
  hasNoEffectLabel,
}: ClassAllocationMethodPropertyProps) => {
  const { wipConfig, basePropertyKey, fullPropertyKey } = basePropertyParams;
  const method = getComponentClassAllocationMethod(basePropertyKey);
  const options = formatSelectOptions<any>(method, 'sentence');

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
      hasNoEffectLabel={hasNoEffectLabel}
    >
      {`${property.value}`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <Flex alignItems="flex-end" width="100%">
              <FormFieldLabel labelWidth="60" label="Value" />
              <InputGroup width="100%">
                <Select
                  value={localFormProps.values.value}
                  onChange={(val) => {
                    localFormProps.setFieldValue('value', val);
                  }}
                  options={options}
                  width="sm"
                />
              </InputGroup>
            </Flex>
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

type ClassLabelPropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  property: CreditAgreement.DomainEntity.ClassLabelProperty;
  basePropertyParams: BasePropertyParams;
  columnWidth?: number;
  accordion?: boolean;
};
const ClassLabelProperty = ({
  headers,
  property,
  basePropertyParams,
  columnWidth,
  accordion,
}: ClassLabelPropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;

  const selectOptions = formatSelectOptions(
    CreditFacility.ClassLabel,
    'sentence',
  );

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
    >
      {`${property.value}`.toUpperCase()}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <Flex alignItems="flex-end" width="100%">
              <FormFieldLabel labelWidth="60" label="Value" />
              <InputGroup width="100%">
                <Select
                  value={localFormProps.values.value}
                  onChange={(val) => {
                    localFormProps.setFieldValue('value', val);
                  }}
                  options={selectOptions}
                  width="sm"
                />
              </InputGroup>
            </Flex>
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

type CurrencyPropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  property: CreditAgreement.DomainEntity.CurrencyProperty;
  basePropertyParams: BasePropertyParams;
  columnWidth?: number;
  accordion?: boolean;
  hasNoEffectLabel?: boolean;
};
const CurrencyProperty = ({
  headers,
  property,
  basePropertyParams,
  columnWidth,
  accordion,
  hasNoEffectLabel,
}: CurrencyPropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;

  const selectOptions = formatSelectOptions(Bank.Currency);

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
      hasNoEffectLabel={hasNoEffectLabel}
    >
      {commonUtils.numberToCurrencyWithSymbol({
        number: property.value,
        currency: commonTypeguards.toEnum<Bank.Currency>(property.currency),
        style: 'code',
        format: '%v %s',
      })}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <>
              <NumberInputFormField
                propName="value"
                label="Value"
                formProps={localFormProps}
                labelWidth="60"
                inputWidth="sm"
                flexDirection="row"
                alignItems="flex-end"
              />
              <Flex width="100%" alignItems="flex-end">
                <FormFieldLabel labelWidth="60" label="Currency" />
                <InputGroup width="100%">
                  <Select
                    value={localFormProps.values.currency}
                    onChange={(val) => {
                      localFormProps.setFieldValue('currency', val);
                    }}
                    options={selectOptions}
                    width="sm"
                  />
                </InputGroup>
              </Flex>
            </>
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

type DatePropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  property: CreditAgreement.DomainEntity.DateProperty;
  basePropertyParams: BasePropertyParams;
  columnWidth?: number;
  accordion?: boolean;
};
const DateProperty = ({
  headers,
  property,
  basePropertyParams,
  columnWidth,
  accordion,
}: DatePropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;
  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
    >
      {`${commonDate.fullDateStrToStr(property.value, 'M/dd/yyyy')}`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <InputFormField
              propName="value"
              label="Value"
              type="date"
              formProps={localFormProps}
              labelWidth="60"
              inputWidth="sm"
              flexDirection="row"
              alignItems="flex-end"
            />
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

type FrequencyPeriodPropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  property: CreditAgreement.DomainEntity.FrequencyPeriodProperty;
  basePropertyParams: BasePropertyParams;
  columnWidth?: number;
  accordion?: boolean;
  selectOptions: Util.FrequencyPeriod[];
};
const FrequencyPeriodProperty = ({
  headers,
  property,
  basePropertyParams,
  columnWidth,
  accordion,
  selectOptions,
}: FrequencyPeriodPropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;

  const frequencyPeriodSelectOptions = formatSelectOptions(
    selectOptions,
    'sentence',
  );

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
    >
      {`${property.value}`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <>
              <Flex alignItems="flex-end" width="100%">
                <FormFieldLabel labelWidth="60" label="Period" />
                <InputGroup width="100%">
                  <Select
                    value={localFormProps.values.value}
                    onChange={(val) => {
                      localFormProps.setFieldValue('value', val);
                    }}
                    options={frequencyPeriodSelectOptions}
                    width="sm"
                  />
                </InputGroup>
              </Flex>
            </>
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

const getSelectOptionsForFrequencyProperty = (
  propertyKey: string,
): {
  frequencyPeriodOptions: CreditAgreement.FrequencyPeriod[];
  frequencyTypeOptions: CreditAgreement.FrequencyType[];
} => {
  let frequencyPeriodOptions: CreditAgreement.FrequencyPeriod[];
  let frequencyTypeOptions: CreditAgreement.FrequencyType[];

  switch (propertyKey) {
    case 'maximumFrequency': {
      frequencyPeriodOptions = [
        CreditAgreement.FrequencyPeriod.Day,
        CreditAgreement.FrequencyPeriod.Week,
        CreditAgreement.FrequencyPeriod.Month,
      ];
      frequencyTypeOptions = [CreditAgreement.FrequencyType.Calendar];
      break;
    }
    default:
      throw new commonError.FinleyError(
        `could not get frequency period/type options for propertyKey: ${propertyKey}`,
      );
  }

  return { frequencyPeriodOptions, frequencyTypeOptions };
};

type FrequencyPropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  property: CreditAgreement.DomainEntity.FrequencyProperty;
  basePropertyParams: BasePropertyParams;
  columnWidth?: number;
  accordion?: boolean;
  hasNoEffectLabel?: boolean;
};
const FrequencyProperty = ({
  headers,
  property,
  basePropertyParams,
  columnWidth,
  accordion,
  hasNoEffectLabel,
}: FrequencyPropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;
  const {
    frequencyPeriodOptions,
    frequencyTypeOptions,
  } = getSelectOptionsForFrequencyProperty(basePropertyParams.propertyKey);

  const frequencyPeriodSelectOptions = formatSelectOptions(
    frequencyPeriodOptions,
    'sentence',
  );
  const frequencyTypeSelectOptions = formatSelectOptions(
    frequencyTypeOptions,
    'sentence',
  );

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
      hasNoEffectLabel={hasNoEffectLabel}
    >
      {`${property.value} per ${property.frequencyPeriod} (${property.frequencyType})`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <>
              <NumberInputFormField
                propName="value"
                label="Value"
                formProps={localFormProps}
                labelWidth="60"
                inputWidth="sm"
                flexDirection="row"
                alignItems="flex-end"
              />
              <Flex alignItems="flex-end" width="100%">
                <FormFieldLabel labelWidth="60" label="Frequency Type" />
                <InputGroup width="100%">
                  <Select
                    isDisabled={frequencyTypeSelectOptions.length <= 1}
                    value={localFormProps.values.frequencyType}
                    onChange={(val) => {
                      localFormProps.setFieldValue('frequencyType', val);
                    }}
                    options={frequencyTypeSelectOptions}
                    width="sm"
                  />
                </InputGroup>
              </Flex>
              <Flex alignItems="flex-end" width="100%">
                <FormFieldLabel labelWidth="60" label="Frequency Period" />
                <InputGroup width="100%">
                  <Select
                    value={localFormProps.values.frequencyPeriod}
                    onChange={(val) => {
                      localFormProps.setFieldValue('frequencyPeriod', val);
                    }}
                    options={frequencyPeriodSelectOptions}
                    width="sm"
                  />
                </InputGroup>
              </Flex>
            </>
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

/**
 * LinkedAccountProperty for funding request component <-> bank account mapping
 */
type LinkedAccountPropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  property: CreditAgreement.DomainEntity.LinkedAccountProperty;
  basePropertyParams: BasePropertyParams;
  columnWidth?: number;
  accordion?: boolean;
};
const LinkedAccountProperty = ({
  headers,
  property,
  basePropertyParams,
  columnWidth,
  accordion,
}: LinkedAccountPropertyProps) => {
  const {
    wipConfig,
    basePropertyKey,
    propertyKey,
    idx,
    fullPropertyKey,
  } = basePropertyParams;
  const { section, description, _note_, _isWrittenIn_ } = property;
  const [isOpen, setIsOpen] = useState(false);
  const currentAccountId = property.value;
  const columnWidthStr = `${columnWidth}%`;
  const { parties } = useCreditAgreementContext();

  const bankAccountsById = useMemo(() => {
    if (wipConfig) {
      return wipConfig.bankAccountsMap;
    }
    return new Map(
      [...(parties.borrowers ?? []), ...(parties.capitalProviders ?? [])]
        .flatMap((entity) => entity.bankAccounts)
        .map((account) => [account.entity.id, account]),
    );
  }, [parties.borrowers, parties.capitalProviders, wipConfig]);
  const currentBankAccountEntity = bankAccountsById.get(currentAccountId);

  return (
    <Tr>
      <Td w={columnWidthStr}>
        <Box>{currentBankAccountEntity?.entity.name || '-'}</Box>
      </Td>
      <Td w={columnWidthStr}>
        {commonUtils.toSentenceCase(
          currentBankAccountEntity?.type.value ?? '',
        ) || '-'}
      </Td>
      {_.includes(headers, 'section') && (
        <Td w={columnWidthStr}>{section || '-'}</Td>
      )}
      {_.includes(headers, 'description') && (
        <Td w={columnWidthStr}>{description || '-'}</Td>
      )}
      {_.includes(headers, '_note_') && (
        <Td w={columnWidthStr}>{_note_ || '-'}</Td>
      )}
      {_.includes(headers, '_isWrittenIn_') && (
        <Td w={columnWidthStr}>
          <>{getBooleanSymbol(_isWrittenIn_)}</>
        </Td>
      )}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <Td>
          <EditButton setIsOpen={setIsOpen} />
          {_.isNumber(idx) && (
            <DeleteArrayPropertyButton
              wipConfig={wipConfig}
              basePropertyKey={`${basePropertyKey}.${propertyKey}.contents`}
              idx={idx}
            />
          )}
          <EditLinkedAccountModal
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            bankAccountsById={bankAccountsById}
            basePropertyParams={basePropertyParams}
            property={property}
          />
        </Td>
      )}
      {accordion && <Td w="5%" />}
    </Tr>
  );
};

type MaximumPrefundingAmountConfigProps = {
  localFormProps: FormikProps<any>;
  validationErrors: string[];
  setValidationErrors: React.Dispatch<React.SetStateAction<string[]>>;
};
/**
 * maximum prefunding amount config ui component
 * @param localFormProps
 * @param validationErrors
 * @param setValidationErrors
 * @constructor
 */
const MaximumPrefundingAmountConfig = ({
  localFormProps,
  validationErrors,
  setValidationErrors,
}: MaximumPrefundingAmountConfigProps) => {
  return (
    <>
      <Flex width="100%">
        <FormLabel w="60">Formula Engine</FormLabel>
        <InputGroup width="100%">
          <Select
            value={
              localFormProps.values._config_.maximumPrefundingAmountConfig
                .config.engineVersion
            }
            isDisabled={engineTypeOptions.length < 2}
            options={engineTypeOptions}
            onChange={(engineVersion) => {
              localFormProps.setFieldValue(
                '_config_.maximumPrefundingAmountConfig.config.engineVersion',
                engineVersion,
              );
            }}
          />
        </InputGroup>
      </Flex>
      {!_.isEmpty(validationErrors) && (
        <Flex width="100%">
          <Alert status="error">
            <AlertIcon />
            <AlertDescription>
              Errors:
              {validationErrors.join(',')}
            </AlertDescription>
          </Alert>
        </Flex>
      )}
      <Flex width="100%">
        <FormLabel w="60">Formula</FormLabel>
        <FormulaEditor
          language="formula-maximumPrefundingAmount"
          resize="vertical"
          value={
            localFormProps.values._config_.maximumPrefundingAmountConfig.config
              .formula
          }
          onChange={(formula) => {
            localFormProps.setFieldValue(
              '_config_.maximumPrefundingAmountConfig.config.formula',
              formula,
            );

            const maximumPrefundingAmountConfig = _.set(
              {
                ...localFormProps.values._config_.maximumPrefundingAmountConfig,
              },
              'config.formula',
              formula,
            );

            setValidationErrors(
              checkMaximumPrefundingAmountConfigErrors({
                maximumPrefundingAmountConfig,
              }),
            );
          }}
        />
      </Flex>
    </>
  );
};

type MaximumPrefundingAmountPropertyProps = {
  property: CreditAgreement.DomainEntity.MaximumPrefundingAmountProperty;
  basePropertyParams: BasePropertyParams;
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  columnWidth?: number;
  accordion?: boolean;
  hasNoEffectLabel?: boolean;
};
const MaximumPrefundingAmountProperty = ({
  property,
  basePropertyParams,
  headers,
  columnWidth,
  accordion,
  hasNoEffectLabel,
}: MaximumPrefundingAmountPropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;
  const [validationErrors, setValidationErrors] = useState(
    wipConfig
      ? checkMaximumPrefundingAmountConfigErrors({
          maximumPrefundingAmountConfig:
            property._config_.maximumPrefundingAmountConfig,
        })
      : [],
  );
  const configChildren = (localFormProps: FormikProps<any>) => {
    return (
      <MaximumPrefundingAmountConfig
        localFormProps={localFormProps}
        validationErrors={validationErrors}
        setValidationErrors={setValidationErrors}
      />
    );
  };

  const onSave = () => {
    finleyAssert(
      !validationErrors.length,
      new commonError.FinleyError(
        'Cannot save maximum prefunding amount formula with errors',
      ),
    );

    const wip = commonUtils.ensure(wipConfig, 'Expected wipConfig to exist');
    return wip.formProps.values;
  };

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
      hasNoEffectLabel={hasNoEffectLabel}
    >
      {`${property.value}`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{
            ...basePropertyParams,
            wipConfig,
            configChildren,
          }}
          onSave={onSave}
        >
          {(localFormProps) => (
            <>
              <InputFormField
                propName="value"
                label="Value"
                formProps={localFormProps}
                labelWidth="60"
                inputWidth="sm"
                flexDirection="row"
                alignItems="flex-end"
              />
            </>
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

/**
 * get options for NumberProperty
 *
 * Submission Notice Period only supports a unit type of `businessDay` since funding requests only settle on
 * business days
 * @param propertyKey
 */
const getUnitTypesForNumberProperty = (
  propertyKey: string,
): CreditAgreement.DomainEntity.NumberPropertyUnit[] => {
  switch (propertyKey) {
    case 'expireDaysOffset':
    case 'submissionNoticePeriod':
      return [CreditAgreement.DomainEntity.NumberPropertyUnit.BusinessDay];

    case 'autoApprove':
      return [
        CreditAgreement.DomainEntity.NumberPropertyUnit.Hour,
        CreditAgreement.DomainEntity.NumberPropertyUnit.Hour24,
      ];

    case 'autoSettleHour':
      return [CreditAgreement.DomainEntity.NumberPropertyUnit.Hour24];

    case 'maximumBorrowingBaseReportDaysOld':
      return [CreditAgreement.DomainEntity.NumberPropertyUnit.Day];

    default:
      return _.values(CreditAgreement.DomainEntity.NumberPropertyUnit);
  }
};

type NumberPropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  property: CreditAgreement.DomainEntity.NumberProperty;
  basePropertyParams: BasePropertyParams;
  columnWidth?: number;
  accordion?: boolean;
  hasNoEffectLabel?: boolean;
};
const NumberProperty = ({
  headers,
  property,
  basePropertyParams,
  columnWidth,
  accordion,
  hasNoEffectLabel,
}: NumberPropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;
  const unitTypes: CreditAgreement.DomainEntity.NumberPropertyUnit[] = getUnitTypesForNumberProperty(
    basePropertyParams.propertyKey,
  );
  const unitTypeSelectOptions = formatSelectOptions(unitTypes, 'sentence');
  const pluralUnit =
    property.value !== 1 && property.unit !== 'hour24'
      ? `(${property.unit}s)`
      : `(${property.unit})`;

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
      hasNoEffectLabel={hasNoEffectLabel}
    >
      {`${property.value} ${pluralUnit}`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <>
              <NumberInputFormField
                propName="value"
                label="Value"
                formProps={localFormProps}
                labelWidth="60"
                inputWidth="sm"
                flexDirection="row"
                alignItems="flex-end"
              />
              <Flex alignItems="flex-end" width="100%">
                <FormFieldLabel labelWidth="60" label="Unit" />
                <InputGroup width="100%">
                  <Select
                    value={localFormProps.values.unit}
                    onChange={(val) => {
                      localFormProps.setFieldValue('unit', val);
                    }}
                    options={unitTypeSelectOptions}
                    width="sm"
                  />
                </InputGroup>
              </Flex>
            </>
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

type PercentageRatePropertyProps = {
  property: CreditAgreement.DomainEntity.PercentageRateProperty;
  basePropertyParams: BasePropertyParams;
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  columnWidth?: number;
  accordion?: boolean;
};
const PercentageRateProperty = ({
  property,
  basePropertyParams,
  headers,
  columnWidth,
  accordion,
}: PercentageRatePropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;

  const value = utils.numberToPercentageLabel(property.value);
  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
    >
      {`${value}`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <NumberInputFormField
              propName="value"
              label="Value"
              formProps={localFormProps}
              labelWidth="60"
              inputWidth="sm"
              flexDirection="row"
              alignItems="flex-end"
            />
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

type InterestRateBenchmarkPropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  property: CreditAgreement.DomainEntity.InterestRateBenchmarkProperty;
  basePropertyParams: BasePropertyParams;
  columnWidth?: number;
  accordion?: boolean;
};
const InterestRateBenchmarkProperty = ({
  headers,
  property,
  basePropertyParams,
  columnWidth,
  accordion,
}: InterestRateBenchmarkPropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;

  const selectOptions = formatSelectOptions(Bank.InterestRateBenchmark);

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
    >
      {`${property.value}`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <>
              <Flex alignItems="flex-end" width="100%">
                <FormFieldLabel labelWidth="60" label="Value" />
                <InputGroup width="100%">
                  <Select
                    value={localFormProps.values.value}
                    onChange={(val) => {
                      localFormProps.setFieldValue('value', val);
                    }}
                    options={selectOptions}
                    width="sm"
                  />
                </InputGroup>
              </Flex>
            </>
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

type RecurringDatePropertyProps = {
  property: CreditAgreement.DomainEntity.RecurringDateProperty;
  basePropertyParams: BasePropertyParams;
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  columnWidth?: number;
  // eslint-disable-next-line react/no-unused-prop-types
  accordion?: boolean;
  hasNoEffectLabel?: boolean;
};
const RecurringDateProperty = ({
  property,
  basePropertyParams,
  headers,
  columnWidth,
  hasNoEffectLabel,
}: RecurringDatePropertyProps) => {
  const { name, section, description, _note_, _isWrittenIn_ } = property;
  const { fullPropertyKey, wipConfig } = basePropertyParams;
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const columnWidthStr = `${columnWidth}%`;

  return (
    <>
      <Tr>
        <Td w={columnWidthStr}>
          <Box>{name}</Box>
          {hasNoEffectLabel ? <NoEffectTooltip /> : null}
        </Td>
        <Td w={columnWidthStr}>{`${property.value}`}</Td>
        {_.includes(headers, 'section') && (
          <Td w={columnWidthStr}>{section || '-'}</Td>
        )}
        {_.includes(headers, 'description') && (
          <Td w={columnWidthStr}>{description || '-'}</Td>
        )}
        {_.includes(headers, '_note_') && (
          <Td w={columnWidthStr}>{_note_ || '-'}</Td>
        )}
        {_.includes(headers, '_isWrittenIn_') && (
          <Td w={columnWidthStr}>
            <>{getBooleanSymbol(_isWrittenIn_)}</>
          </Td>
        )}
        {wipConfig && (
          <Td w={columnWidthStr}>
            <MutateProperty
              mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
            >
              {(localFormProps) => (
                <InputFormField
                  propName="value"
                  label="Value"
                  formProps={localFormProps}
                  labelWidth="60"
                  inputWidth="sm"
                  flexDirection="row"
                  alignItems="flex-end"
                />
              )}
            </MutateProperty>
          </Td>
        )}
        <Td width="5%">
          {isConfigOpen ? (
            <IconButton
              aria-label="close config"
              icon={<ChevronUpIcon />}
              onClick={() => setIsConfigOpen(false)}
              variant="ghost"
            />
          ) : (
            <IconButton
              aria-label="display config"
              icon={<ChevronDownIcon />}
              onClick={() => setIsConfigOpen(true)}
              variant="ghost"
            />
          )}
        </Td>
      </Tr>
      {isConfigOpen && (
        <Tr>
          <Td max-width="95%">
            <RecurringDateConfigView
              fullPropertyKey={`${fullPropertyKey}._config_.recurringDateConfig`}
              recurringDateConfig={property._config_.recurringDateConfig}
              wipConfig={wipConfig}
              enableEdit
            />
          </Td>
        </Tr>
      )}
    </>
  );
};

type ReportTypePropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  property: CreditAgreement.DomainEntity.ReportTypeProperty;
  basePropertyParams: BasePropertyParams;
  columnWidth?: number;
  accordion?: boolean;
};
const ReportTypeProperty = ({
  headers,
  property,
  basePropertyParams,
  columnWidth,
  accordion,
}: ReportTypePropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;

  const selectOptions = formatSelectOptions(
    Report.ReportTypeIterable,
    'sentence',
  );

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
    >
      {`${property.value}`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <Flex alignItems="flex-end" width="100%">
              <FormFieldLabel labelWidth="60" label="Value" />
              <InputGroup width="100%">
                <Select
                  value={localFormProps.values.value}
                  onChange={(val) => {
                    localFormProps.setFieldValue('value', val);
                  }}
                  options={selectOptions}
                  width="sm"
                />
              </InputGroup>
            </Flex>
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

type StructurePropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  property: CreditAgreement.DomainEntity.StructureProperty;
  basePropertyParams: BasePropertyParams;
  columnWidth?: number;
  accordion?: boolean;
};
const StructureProperty = ({
  headers,
  property,
  basePropertyParams,
  columnWidth,
  accordion,
}: StructurePropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;

  const selectOptions = formatSelectOptions(
    CreditFacility.CreditFacilityStructureType,
    'sentence',
  );

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
    >
      {`${property.value}`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <Flex alignItems="flex-end" width="100%">
              <FormFieldLabel labelWidth="60" label="Value" />
              <InputGroup width="100%">
                <Select
                  value={localFormProps.values.value}
                  onChange={(val) => {
                    localFormProps.setFieldValue('value', val);
                  }}
                  options={selectOptions}
                  width="sm"
                />
              </InputGroup>
            </Flex>
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

type TextPropertyProps = {
  property: CreditAgreement.DomainEntity.TextProperty;
  basePropertyParams: BasePropertyParams;
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  columnWidth?: number;
  accordion?: boolean;
};
const TextProperty = ({
  property,
  basePropertyParams,
  headers,
  columnWidth,
  accordion,
}: TextPropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
    >
      {`${property.value}`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <InputFormField
              propName="value"
              label="Value"
              formProps={localFormProps}
              labelWidth="60"
              inputWidth="sm"
              flexDirection="row"
              alignItems="flex-end"
            />
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

/**
 * this function must have an exhaustive set of all property keys for FrequencyPeriodProperty items in order to show
 * only the valid select options in the UI
 * @param frequencyPeriodPropertyKey
 */
const getSelectOptionsForFrequencyPeriodProperty = (
  frequencyPeriodPropertyKey: string,
): Util.FrequencyPeriod[] => {
  switch (frequencyPeriodPropertyKey) {
    case 'interestRateBenchmarkPeriod':
      return [Util.FrequencyPeriod.Day, Util.FrequencyPeriod.Month];
    case 'maximumAmountPeriod':
      return [Util.FrequencyPeriod.Week, Util.FrequencyPeriod.Month];
    default:
      throw new commonError.FinleyError(
        `unable to get select options for property key: ${frequencyPeriodPropertyKey}`,
      );
  }
};

type DefaultPropertyProps = {
  property: any;
  basePropertyParams: BasePropertyParams;
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  columnWidth?: number;
  accordion?: boolean;
};
const DefaultProperty = ({
  property,
  basePropertyParams,
  headers,
  columnWidth,
  accordion,
}: DefaultPropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
    >
      {`${property.value}`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <InputFormField
              propName="value"
              label="Value"
              formProps={localFormProps}
              labelWidth="60"
              inputWidth="sm"
              flexDirection="row"
              alignItems="flex-end"
            />
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

type AutoApprovalPropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  property: CreditAgreement.DomainEntity.AutoApprovalProperty;
  basePropertyParams: BasePropertyParams;
  columnWidth?: number;
  accordion?: boolean;
  hasNoEffectLabel?: boolean;
};
const AutoApprovalProperty = ({
  headers,
  property,
  basePropertyParams,
  columnWidth,
  accordion,
  hasNoEffectLabel,
}: AutoApprovalPropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;
  const approvalStrategies: CreditAgreement.DomainEntity.AutoApprovalStrategy[] = _.values(
    CreditAgreement.DomainEntity.AutoApprovalStrategy,
  );
  const approvalStrategySelectOptions = formatSelectOptions(
    approvalStrategies,
    'sentence',
  );

  const unitTypes: CreditAgreement.DomainEntity.NumberPropertyUnit[] = getUnitTypesForNumberProperty(
    basePropertyParams.propertyKey,
  );
  const unitTypeSelectOptions = formatSelectOptions(unitTypes, 'sentence');

  let displayValue = `${property.approvalStrategy} ${property.value} ${property.unit}`;
  switch (property.approvalStrategy) {
    case CreditAgreement.DomainEntity.AutoApprovalStrategy.SpecificHour:
      displayValue = `Auto-approve if submitted by ${property.value % 12}:00${
        property.value < 12 ? 'AM' : 'PM'
      } (ET)`;
      break;
    case CreditAgreement.DomainEntity.AutoApprovalStrategy.WithDelay:
      displayValue = `Auto-approve with ${property.value} hour delay after submission`;
      break;
    default:
      finleyAssertUnreachable(property.approvalStrategy);
  }

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
      hasNoEffectLabel={hasNoEffectLabel}
    >
      {displayValue}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <>
              <Flex alignItems="flex-end" width="100%">
                <FormFieldLabel labelWidth="60" label="Approval Strategy" />
                <InputGroup width="100%">
                  <Select
                    value={localFormProps.values.approvalStrategy}
                    onChange={(val) => {
                      localFormProps.setFieldValue('approvalStrategy', val);
                      if (
                        val ===
                        CreditAgreement.DomainEntity.AutoApprovalStrategy
                          .SpecificHour
                      ) {
                        localFormProps.setFieldValue(
                          'unit',
                          CreditAgreement.DomainEntity.NumberPropertyUnit
                            .Hour24,
                        );
                      } else if (
                        val ===
                        CreditAgreement.DomainEntity.AutoApprovalStrategy
                          .WithDelay
                      ) {
                        localFormProps.setFieldValue(
                          'unit',
                          CreditAgreement.DomainEntity.NumberPropertyUnit.Hour,
                        );
                      }
                    }}
                    options={approvalStrategySelectOptions}
                    width="sm"
                  />
                </InputGroup>
              </Flex>
              <NumberInputFormField
                propName="value"
                label="Value"
                formProps={localFormProps}
                labelWidth="60"
                inputWidth="sm"
                flexDirection="row"
                alignItems="flex-end"
              />
              <Flex alignItems="flex-end" width="100%">
                <FormFieldLabel labelWidth="60" label="Unit" />
                <InputGroup width="100%">
                  <Select
                    value={localFormProps.values.unit}
                    onChange={(val) => {
                      localFormProps.setFieldValue('unit', val);
                    }}
                    isDisabled
                    options={unitTypeSelectOptions}
                    width="sm"
                  />
                </InputGroup>
              </Flex>
            </>
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

type AttachmentRateLimitPropertyProps = {
  property: CreditAgreement.DomainEntity.AttachmentRateLimitProperty;
  basePropertyParams: BasePropertyParams;
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  columnWidth?: number;
  accordion?: boolean;
};
const AttachmentRateLimitProperty = ({
  property,
  basePropertyParams,
  headers,
  columnWidth,
  accordion,
}: AttachmentRateLimitPropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
    >
      {`${property.value}`}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(
            localFormProps: FormikProps<CreditAgreement.DomainEntity.AttachmentRateLimitProperty>,
          ) => {
            return (
              <Box id="attachment-rate-formula-editor" w="100%">
                <Box mb="1">
                  <Text fontWeight="500">Attachment Rate Formula</Text>
                  <Text as="i" fontSize="sm" color="gray">
                    Please do not include initial &quot;=&quot; in below formula
                  </Text>
                </Box>
                <Editor
                  language="formula-attachmentRateLimit"
                  value={localFormProps.values._config_.config.formula}
                  onChange={(val) => {
                    localFormProps.setFieldValue(
                      '_config_.config.formula',
                      val ?? '',
                    );
                    // "Value" for attachment rate property is not used at all and
                    // currently just represents another description area (no impact).
                    // In this case, we'll just default it to the used formula for now
                    // since we're not exposing ability to edit the value anyways
                    localFormProps.setFieldValue('value', val ?? '');
                  }}
                  height="200px"
                  theme={defaultTheme}
                  options={{
                    fixedOverflowWidgets: true,
                    minimap: { enabled: false },
                  }}
                />
              </Box>
            );
          }}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

type OvercollateralizationAmountPropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  property: CreditAgreement.DomainEntity.OvercollateralizationAmountProperty;
  basePropertyParams: BasePropertyParams;
  columnWidth?: number;
  accordion?: boolean;
  hasNoEffectLabel?: boolean;
};
const OvercollateralizationAmountProperty = ({
  headers,
  property,
  basePropertyParams,
  columnWidth,
  accordion,
  hasNoEffectLabel,
}: OvercollateralizationAmountPropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;

  const valueTypes: CreditAgreement.DomainEntity.OvercollateralizationAmountProperty['valueType'][] = [
    'percent',
  ];
  const valueTypeSelectOptions = formatSelectOptions(valueTypes, 'sentence');

  // Fixed to max 3 decimal place output (but can handle an arbitrary amount)
  const convertDecimalToStringPercent = (num: number): string => {
    // Enforce minimum 2 decimal places in string, i.e. value is 1 instead of 1.0, or 0.50
    const stringPrecision = Math.max(num.toString().length - 1, 2);
    const stringNum = num.toPrecision(stringPrecision);
    const [whole, fraction] = stringNum.split('.');
    return `${(
      (Number(whole) * 10 ** fraction.length + Number(fraction)) /
      10 ** (fraction.length - 2)
    ).toLocaleString(undefined, {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    })}%`;
  };

  let displayValue = `${property.value}`;
  switch (property.valueType) {
    case 'percent':
      displayValue = convertDecimalToStringPercent(property.value);
      break;
    default:
      finleyAssertUnreachable(property.valueType);
  }

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
      hasNoEffectLabel={hasNoEffectLabel}
    >
      {displayValue}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <>
              <NumberInputFormField
                propName="value"
                label="Value"
                formProps={localFormProps}
                labelWidth="60"
                inputWidth="sm"
                flexDirection="row"
                alignItems="flex-end"
              />
              <Flex alignItems="flex-end" width="100%">
                <FormFieldLabel labelWidth="60" label="Type" />
                <InputGroup width="100%">
                  <Select
                    value={localFormProps.values.valueType}
                    onChange={(val) => {
                      localFormProps.setFieldValue('valueType', val);
                    }}
                    isDisabled
                    options={valueTypeSelectOptions}
                    width="sm"
                  />
                </InputGroup>
              </Flex>
            </>
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

type OvercollateralizationMethodPropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  property: CreditAgreement.DomainEntity.OvercollateralizationMethodProperty;
  basePropertyParams: BasePropertyParams;
  columnWidth?: number;
  accordion?: boolean;
  hasNoEffectLabel?: boolean;
};
const OvercollateralizationMethodProperty = ({
  headers,
  property,
  basePropertyParams,
  columnWidth,
  accordion,
  hasNoEffectLabel,
}: OvercollateralizationMethodPropertyProps) => {
  const { wipConfig, fullPropertyKey } = basePropertyParams;
  const methodTypes: CreditAgreement.DomainEntity.OvercollateralizationMethod[] = _.values(
    CreditAgreement.DomainEntity.OvercollateralizationMethod,
  );
  const valueTypeSelectOptions = formatSelectOptions(methodTypes, 'sentence');

  let displayValue = `${property.value}`;
  let description = 'Calculation method for overcollateralization';
  switch (property.value) {
    case CreditAgreement.DomainEntity.OvercollateralizationMethod
      .ClassSubordination:
      displayValue = 'Class Subordination';
      description =
        'Maximum of required amounts by class, where each class value is calculated as the cumulative sum of outstanding balance (includes senior classes), minus collections balance';
      break;
    default:
      finleyAssertUnreachable(property.value);
  }

  // eslint-disable-next-line no-param-reassign
  property.description = description;

  return (
    <BasePropertyTableRow
      property={property}
      headers={headers}
      columnWidth={columnWidth}
      accordion={accordion}
      hasNoEffectLabel={hasNoEffectLabel}
    >
      {displayValue}
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <MutateProperty
          mutatePropertyParams={{ ...basePropertyParams, wipConfig }}
        >
          {(localFormProps) => (
            <>
              <Flex alignItems="flex-end" width="100%">
                <FormFieldLabel labelWidth="60" label="Type" />
                <InputGroup width="100%">
                  <Select
                    value={localFormProps.values.value}
                    onChange={(val) => {
                      localFormProps.setFieldValue('value', val);
                    }}
                    isDisabled
                    options={valueTypeSelectOptions}
                    width="sm"
                  />
                </InputGroup>
              </Flex>
            </>
          )}
        </MutateProperty>
      )}
    </BasePropertyTableRow>
  );
};

type PropertyProps = {
  headers: (
    | keyof CreditAgreement.DomainEntity.Property
    | keyof CreditAgreement.DomainEntity.PropertyContainer
  )[];
  basePropertyKey: string;
  property: CreditAgreement.DomainEntity.Property;
  propertyKey: string;
  isOptional: boolean;
  wipConfig?: WipConfig;
  idx?: number;
  columnWidth?: number;
  accordion?: boolean;
};
export const Property = ({
  headers,
  property,
  propertyKey,
  wipConfig,
  basePropertyKey,
  isOptional,
  idx,
  columnWidth,
  accordion = false,
}: PropertyProps) => {
  const fullPropertyKey = _.isNumber(idx)
    ? `${basePropertyKey}.${propertyKey}.contents[${idx}]`
    : `${basePropertyKey}.${propertyKey}`;

  const { type } = property;
  const hasNoEffectLabel = checkHasNoEffect({ basePropertyKey, propertyKey });

  const basePropertyParams: BasePropertyParams = {
    propertyKey,
    wipConfig,
    isOptional,
    basePropertyKey,
    fullPropertyKey,
    idx,
  };

  switch (type) {
    case CreditAgreement.DomainEntity.PropertyType.BankAccountTypeProperty: {
      return (
        <BankAccountProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
        />
      );
    }
    case CreditAgreement.DomainEntity.PropertyType.BooleanProperty:
      return (
        <BooleanProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
          hasNoEffectLabel={hasNoEffectLabel}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType.CashAdvanceRateProperty:
      return (
        <CashAdvanceRateProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType
      .ClassAllocationMethodProperty:
      return (
        <ClassAllocationMethodProperty
          headers={headers}
          property={property}
          basePropertyParams={basePropertyParams}
          columnWidth={columnWidth}
          accordion={accordion}
          hasNoEffectLabel={hasNoEffectLabel}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType.ClassLabelProperty:
      return (
        <ClassLabelProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType.CurrencyProperty:
      return (
        <CurrencyProperty
          basePropertyParams={basePropertyParams}
          property={property}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
          hasNoEffectLabel={hasNoEffectLabel}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType.DateProperty:
      return (
        <DateProperty
          headers={headers}
          property={property}
          basePropertyParams={basePropertyParams}
          columnWidth={columnWidth}
          accordion={accordion}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType.FrequencyPeriodProperty:
      return (
        <FrequencyPeriodProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
          selectOptions={getSelectOptionsForFrequencyPeriodProperty(
            propertyKey,
          )}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType.FrequencyProperty:
      return (
        <FrequencyProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
          hasNoEffectLabel={hasNoEffectLabel}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType
      .InterestRateBenchmarkProperty:
      return (
        <InterestRateBenchmarkProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType.AutoApprovalProperty:
      return (
        <AutoApprovalProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
          hasNoEffectLabel={hasNoEffectLabel}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType.LinkedAccountProperty:
      return (
        <LinkedAccountProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType
      .MaximumPrefundingAmountProperty:
      return (
        <MaximumPrefundingAmountProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
          hasNoEffectLabel={hasNoEffectLabel}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType.NumberProperty:
      return (
        <NumberProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
          hasNoEffectLabel={hasNoEffectLabel}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType
      .OvercollateralizationAmountProperty:
      return (
        <OvercollateralizationAmountProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
          hasNoEffectLabel={hasNoEffectLabel}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType
      .OvercollateralizationMethodProperty:
      return (
        <OvercollateralizationMethodProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
          hasNoEffectLabel={hasNoEffectLabel}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType.PercentageRateProperty:
      return (
        <PercentageRateProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType.RecurringDateProperty:
      return (
        <RecurringDateProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
          hasNoEffectLabel={hasNoEffectLabel}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType.ReportTypeProperty:
      return (
        <ReportTypeProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType.StructureProperty:
      return (
        <StructureProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType.TextProperty:
      return (
        <TextProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
        />
      );
    case CreditAgreement.DomainEntity.PropertyType.AttachmentRateLimitProperty:
      return (
        <AttachmentRateLimitProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
        />
      );
    /**
     * The following properties are only used in borrower dashboard to display properties
     */
    case CreditAgreement.DomainEntity.PropertyType
      .ReceivableAdvanceRateProperty:
    case CreditAgreement.DomainEntity.PropertyType
      .ReceivableValuationRateProperty:
    case CreditAgreement.DomainEntity.PropertyType.DeliverableProperty:
    case CreditAgreement.DomainEntity.PropertyType.ConcentrationLimitProperty:
    case CreditAgreement.DomainEntity.PropertyType.EligibilityTestProperty:
      return (
        <DefaultProperty
          property={property}
          basePropertyParams={basePropertyParams}
          headers={headers}
          columnWidth={columnWidth}
          accordion={accordion}
        />
      );
    /**
     * DeliverableReportProperty and eligibilityTestProperty will only be used in the reporting deliverables section and will be used outside the section
     */
    // @ts-ignore
    case CreditAgreement.DomainEntity.PropertyType.DeliverableReportProperty:
    case CreditAgreement.DomainEntity.PropertyType.FinancialCovenantProperty:
      throw Error(`unexpected property type ${type}`);

    default:
      return finleyAssertUnreachable(type);
  }
};
